import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useDebouncedSearchText } from "./useDebouncedSearchText";
import {
  normalizeUsersQueryArgs,
  useGetUsersQuery,
} from "../store/services/users";
import { User, UsersParams, UsersPagination } from "../models/users";

type UsersQueryOptions = Parameters<typeof useGetUsersQuery>[1];
type UsersQueryResult = ReturnType<typeof useGetUsersQuery>;
type TypedUsersQueryResult = Omit<UsersQueryResult, "data"> & {
  data: User[] | undefined;
  pagination: UsersPagination | undefined;
};

type DebouncedUsersQueryParams = Omit<UsersParams, "id"> & {
  id?: number | string;
};

const resolveId = (id: number | string | undefined): number | undefined => {
  if (id == null) return undefined;
  if (typeof id === "number") return id;
  const trimmed = id.trim();
  return trimmed ? Number(trimmed) : undefined;
};

export function useDebouncedUsersQuery(
  params: DebouncedUsersQueryParams | void,
  options?: UsersQueryOptions
): TypedUsersQueryResult {
  const {
    id,
    email,
    firstName,
    lastName,
    userTypeId,
    tenantId,
    banned,
    limit,
    page,
  } = params ?? {};

  const debouncedId = useDebouncedSearchText(typeof id === "string" ? id : undefined);
  const debouncedEmail = useDebouncedSearchText(email);
  const debouncedFirstName = useDebouncedSearchText(firstName);
  const debouncedLastName = useDebouncedSearchText(lastName);

  const isDebouncing =
    (typeof id === "string" ? (id ?? "") : "") !== debouncedId ||
    (email ?? "") !== debouncedEmail ||
    (firstName ?? "") !== debouncedFirstName ||
    (lastName ?? "") !== debouncedLastName;

  const queryArgs = useMemo(
    () =>
      normalizeUsersQueryArgs({
        userTypeId,
        tenantId,
        banned,
        limit,
        page,
        id: typeof id === "string" ? resolveId(debouncedId) : resolveId(id),
        email: debouncedEmail,
        firstName: debouncedFirstName,
        lastName: debouncedLastName,
      }),
    [
      userTypeId,
      tenantId,
      banned,
      limit,
      page,
      id,
      debouncedId,
      debouncedEmail,
      debouncedFirstName,
      debouncedLastName,
    ]
  );

  const shouldSkip = Boolean(options?.skip) || isDebouncing;

  const queryResult = useGetUsersQuery(
    shouldSkip ? skipToken : queryArgs,
    options
  );

  // refetch lanza un error si la query está salteada (debounce pendiente);
  // en ese caso el fetch se dispara solo al asentarse el valor.
  const refetch = (() => {
    if (!shouldSkip) return queryResult.refetch();
  }) as UsersQueryResult["refetch"];

  return {
    ...queryResult,
    refetch,
    data: queryResult.data?.users,
    pagination: queryResult.data?.pagination,
  } as unknown as TypedUsersQueryResult;
}
